// React dependencies
import React, { useEffect, useState } from 'react';

// External dependencies
import { makeStyles, TextField } from '@material-ui/core';
import { MoonLoader } from 'react-spinners';
import { ArrowForward } from '@material-ui/icons';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Internal dependencies
import MText from '../../../../../components/Common/MText';
import Colours from '../../../../../styles/colours';
import { IsEmpty } from '../../../../../common/checks';
import MButton from '../../../../../components/MButton';
import { Sleep, Trim } from '../../../../../common/util';
import ApiService from '../../../../../services/apiService';
import useDebounce from '../../../../../common/use-debounce';

const useStyles = makeStyles((theme) => ({
  root: {
    width:                        '100%',
    [theme.breakpoints.up('sm')]: {
      width: '80%',
    },
  },
  textField: {
    '& .MuiInputBase-root': {
      fontSize: 14,
    },
  },
}));

const PoiName = (props) => {
  const classes = useStyles();
  const [suggestions, setSuggestions] = useState([]);
  const debouncedSearchKeyword = useDebounce(props.searchKeyword, 400);

  const fetchKey = (pageId, keyword) => `${pageId}::${Trim(keyword)}`;
  const currentFetchKey = fetchKey(props.pageSelected.id, props.searchKeyword);
  const [resolvedFetchKey, setResolvedFetchKey] = useState(
    IsEmpty(Trim(props.searchKeyword)) ? currentFetchKey : null,
  );

  const matchingPoi = suggestions.find((suggestion) => Trim(suggestion.name.toLowerCase()) === Trim(props.searchKeyword.toLowerCase()));

  const hasExistingPoi = !IsEmpty(props.selectedPoi) || !IsEmpty(matchingPoi);
  const showLoader = currentFetchKey !== resolvedFetchKey;

  const handlePoiSelection = (poi) => {
    props.onSelectedPoiChange(poi);
    props.onProceedWithExistingPoi(poi);
    props.onSearchKeywordChange(poi?.name || '');
  };

  const fetchLivePoiSuggestions = async () => {
    try {
      const trimmedKeyword = Trim(debouncedSearchKeyword);
      const selectedPoiMatchesKeyword = !IsEmpty(props.selectedPoi)
        && Trim(props.selectedPoi.name.toLowerCase()) === trimmedKeyword.toLowerCase();

      if (IsEmpty(trimmedKeyword)) {
        props.onSelectedPoiChange(null);
        setSuggestions([]);
        return;
      }

      if (selectedPoiMatchesKeyword) {
        return;
      }

      props.onSelectedPoiChange(null);

      const result = await ApiService.getLivePoiSuggestions(props.pageSelected.id, debouncedSearchKeyword, 5);
      await Sleep(1000);
      setSuggestions(result);
    } catch (error) {
      setSuggestions([]);
      toast.error(error.message);
    } finally {
      setResolvedFetchKey(fetchKey(props.pageSelected.id, debouncedSearchKeyword));
    }
  };

  useEffect(() => {
    fetchLivePoiSuggestions();
  }, [debouncedSearchKeyword, props.pageSelected.id]);

  return (
    <div
      className={`d-flex flex-column align-items-center mx-auto mt-5 ${classes.root}`}
    >
      <MText
        text="Where is the cool spot?"
        size={16}
        style={{ fontWeight: 500 }}
      />
      <TextField
        onChange={(e) => props.onSearchKeywordChange(e.target.value)}
        value={props.searchKeyword}
        placeholder="Type name of the place"
        variant="outlined"
        className={`mt-3 ${classes.root} ${classes.textField}`}
        size="small"
        style={{
          fontStyle: (IsEmpty(props.searchKeyword) ? 'italic' : 'normal'),
        }}
      />

      <div className="mt-4 w-100">
        {
          showLoader ? (
            <MoonLoader className="mx-auto" color={Colours.Primary} size={21} />
          ) : (
            <>
              {
                !IsEmpty(suggestions) ? (
                  <>
                    <MText className="align-self-start ml-3" text="Similar Snapstyr locations" />
                    <ul className="border w-100 list-unstyled rounded-lg mt-1 py-2">
                      {
                        suggestions.map((suggestion) => (
                          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                          <li
                            key={suggestion.id}
                            className="py-2 px-4"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handlePoiSelection(suggestion)}
                          >
                            <span
                              style={{
                                wordBreak:       'break-all',
                                display:         '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow:        'hidden',
                              }}
                            >
                              <MText text={suggestion.name} size={13} />
                            </span>
                          </li>
                        ))
                      }
                    </ul>
                  </>
                ) : null
              }
            </>
          )
        }
      </div>
      <div className="mt-4">
        {
          !IsEmpty(props.searchKeyword) && !showLoader && (
            hasExistingPoi ? (
              <MText
                className="text-center"
                text={`There is an existing Snapstyr location '${props.selectedPoi?.name || matchingPoi?.name}'.\nClick to contribute to it.`}
                style={{
                  whiteSpace:     'pre-line',
                  textDecoration: 'underline',
                  cursor:         'pointer',
                }}
                onClick={() => handlePoiSelection(!IsEmpty(props.selectedPoi) ? props.selectedPoi : matchingPoi)}
              />
            ) : (
              <MButton
                disableElevation
                style={{
                  border:       '1px solid black',
                  borderRadius: 12,
                }}
                text={`Proceed with "${props.searchKeyword}"`}
                textClassName="ml-2"
                textSize={15}
                type="button"
                onClick={props.onProceedWithNewPoi}
                className="pl-3 pr-3"
                textStyle={{ fontWeight: 500 }}
                icon={() => <ArrowForward style={{ height: 20 }} />}
              />
            )
          )
        }
      </div>
    </div>
  );
};

PoiName.propTypes = {
  pageSelected: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  searchKeyword: PropTypes.string.isRequired,
  selectedPoi:   PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  onProceedWithNewPoi:      PropTypes.func.isRequired,
  onProceedWithExistingPoi: PropTypes.func.isRequired,
  onSelectedPoiChange:      PropTypes.func.isRequired,
  onSearchKeywordChange:    PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  pageSelected: state.page.selected,
});

export default connect(mapStateToProps, null)(PoiName);
